/** Utils */
import { useCallback, useEffect, useRef } from 'react';
import AuthService from '../service/AuthService';

/** Types */
import type { AuthContextProps } from '../Auth.types';
import { isAllowedAccountType } from '../utils/isAllowedAccountType';
import { logoutChannel } from '../config/logout-channel';

/** CSRF token revalidation interval (30 minutes in milliseconds) */
const CSRF_REVALIDATION_INTERVAL_MS = 30 * 60 * 1000;

const useAuthSync = (props: AuthContextProps): void => {
  const { state, user, isLoggingOut, dispatch, logout } = props;
  const csrfIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleLogoutSync = useCallback(
    async (event: MessageEvent) => {
      if (event.data === 'logout' && !isLoggingOut) {
        // If trying to logout through this channel, it means tokens are already gone.
        // Just wipe the reducer state.
        // First check if state is already wiped, to prevent unnecessary re-rendering.
        if (state !== 'loggedout') {
          dispatch({ type: 'LOGOUT' });
        }
      }
    },
    [dispatch, isLoggingOut, state]
  );

  const handleStorageChangeSync = useCallback(
    async (event: StorageEvent) => {
      if (
        event.key === 'access_token' ||
        event.key === 'refresh_token' ||
        event.key === 'csrf_token' ||
        event.key === 'forced_wipe'
      ) {
        let token = '';
        const { accessToken, refreshToken, csrfToken } = AuthService.getTokens();

        if (accessToken && refreshToken) {
          // Both tokens are present, update the state
          AuthService.setTokens({ accessToken, refreshToken, csrfToken });

          // Find csrf

          // Fetch user if not logged in (probably logged in other tab).
          // If already logged in, just revalidate tokens (probably corrupted tokens).
          if (!user) {
            const userData = await AuthService.getUser({ accessToken });

            if (!isAllowedAccountType(userData.account_type)) {
              AuthService.clearTokens();
              await logout({});
              return;
            }

            /**
             * Don't logout user if token is missing, make new CSRF instead
             */
            if (!csrfToken) {
              const { csrf_token } = await AuthService.CSRFToken();
              token = csrf_token;
            } else {
              token = csrfToken;
            }

            dispatch({
              type: 'LOGIN',
              payload: { user: userData, accessToken, refreshToken, csrfToken: token },
            });
            return;
          }

          dispatch({
            type: 'REVALIDATE_TOKENS',
            payload: { accessToken, refreshToken, csrfToken },
          });
        } else {
          if (!isLoggingOut && state !== 'loggedout') {
            // Tokens are not present, just wipe reducer state and notify other tabs since
            // there's no way to revalidate non-existing tokens.
            await logout({});
          }
        }
      }
    },
    [dispatch, isLoggingOut, logout, state, user]
  );

  /** Handle logout across tabs */
  useEffect(() => {
    logoutChannel.addEventListener('message', handleLogoutSync);
    return () => {
      logoutChannel.removeEventListener('message', handleLogoutSync);
    };
  }, [handleLogoutSync]);

  /** Handle storage changes */
  useEffect(() => {
    window.addEventListener('storage', handleStorageChangeSync);
    return () => {
      window.removeEventListener('storage', handleStorageChangeSync);
    };
  }, [handleStorageChangeSync]);

  /** Revalidate CSRF token every 30 minutes when user is logged in */
  useEffect(() => {
    const revalidateCsrfToken = async () => {
      try {
        const { accessToken, refreshToken } = AuthService.getTokens();

        // Only revalidate if user is authenticated
        if (!accessToken || !refreshToken) return;

        const { csrf_token } = await AuthService.CSRFToken();

        if (csrf_token) {
          dispatch({
            type: 'REVALIDATE_TOKENS',
            payload: { accessToken, refreshToken, csrfToken: csrf_token },
          });
        }
      } catch (error) {
        // Silently fail - CSRF will be refreshed on next interval or user action
        console.warn('Failed to revalidate CSRF token:', error);
      }
    };

    // Only set up interval when user is logged in
    if (user && state === 'loggedin') {
      // Clear any existing interval
      if (csrfIntervalRef.current) {
        clearInterval(csrfIntervalRef.current);
      }

      // Set up new interval
      csrfIntervalRef.current = setInterval(revalidateCsrfToken, CSRF_REVALIDATION_INTERVAL_MS);
    }

    // Cleanup on unmount or when user logs out
    return () => {
      if (csrfIntervalRef.current) {
        clearInterval(csrfIntervalRef.current);
        csrfIntervalRef.current = null;
      }
    };
  }, [user, state, dispatch]);
};

export default useAuthSync;
